import { useCallback } from 'react';
import { scrollToElement, scrollToTop, scrollToBookingForm, isElementInViewport } from '../helpers/smoothScroll';

/**
 * Custom hook for smooth scrolling functionality
 * @returns {Object} Smooth scroll functions
 */
export const useSmoothScroll = () => {
    
    // Scroll to any element by ID
    const scrollTo = useCallback((elementId, offset = 80) => {
        return scrollToElement(elementId, offset);
    }, []);

    // Scroll to top of page
    const scrollTop = useCallback(() => {
        return scrollToTop();
    }, []);

    // Scroll to booking form with fallback to top
    const scrollToBooking = useCallback((headerOffset = 100) => {
        return scrollToBookingForm(headerOffset);
    }, []);

    // Check if element is in viewport
    const isInViewport = useCallback((elementId) => {
        return isElementInViewport(elementId);
    }, []);

    // Smart scroll - goes to booking form if available, otherwise to top
    const smartScroll = useCallback(() => {
        const bookingFormExists = document.getElementById('booking-form');
        
        if (bookingFormExists) {
            return scrollToBooking();
        } else {
            return scrollTop();
        }
    }, [scrollToBooking, scrollTop]);

    // Scroll to specific section with intelligent behavior
    const scrollToSection = useCallback((sectionId, fallbackAction = 'top') => {
        const success = scrollTo(sectionId);
        
        if (!success) {
            switch (fallbackAction) {
                case 'booking':
                    return scrollToBooking();
                case 'top':
                default:
                    return scrollTop();
            }
        }
        
        return success;
    }, [scrollTo, scrollToBooking, scrollTop]);

    return {
        scrollTo,
        scrollTop,
        scrollToBooking,
        smartScroll,
        scrollToSection,
        isInViewport
    };
};

export default useSmoothScroll;
